import { useCallback, useEffect, useId, useRef, useState } from "react";

interface ClampedTextProps {
  text: string;
  /** Lines shown before clamping. */
  lines?: number;
  /** Class for the text itself, so callers keep their own typography. */
  className?: string;
  /**
   * Names the thing being described, for the toggle's accessible label. Someone
   * tabbing through a page of these needs to know which "Read more" is which.
   */
  label: string;
}

/**
 * Text clamped to a few lines, with a toggle that appears only when it is
 * actually cut off.
 *
 * Clamping is CSS, not string truncation: the whole text stays in the DOM, so it
 * is still read by search engines and by a screen reader that walks the page,
 * and it reflows correctly at every width without recalculating a cut-off point.
 *
 * The toggle is conditional on real measurement rather than a character count,
 * because whether four lines is enough depends on the width, the font and the
 * words - a 300-character description might fit on a wide screen and not on a
 * phone, and a button that expands nothing is worse than no button.
 */
export function ClampedText({
  text,
  lines = 4,
  className = "",
  label,
}: ClampedTextProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const id = useId();

  /**
   * Only meaningful while collapsed - expanded, the element is its full height
   * and would always measure as fitting. The flag is left alone in that case and
   * re-checked when it collapses again.
   */
  const measure = useCallback(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    // A pixel of tolerance: sub-pixel line heights otherwise report a phantom
    // overflow on text that visibly fits.
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useEffect(() => {
    measure();

    const el = textRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    // Web fonts change line metrics when they land, which can turn a fitting
    // description into a clipped one after first paint.
    let cancelled = false;
    document.fonts?.ready
      .then(() => {
        if (!cancelled) measure();
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [measure, text, lines]);

  /**
   * Clamped whenever collapsed, not only once overflow is known.
   *
   * Gating this on `overflows` would be circular: the element can only be
   * measured as overflowing while it is clamped, so it would measure as fitting,
   * never clamp, and never show a toggle. Clamping unconditionally makes the
   * measurement meaningful, and costs nothing on text that fits - a four-line
   * clamp on two lines of prose looks exactly like two lines of prose.
   */
  const clamped = !expanded;

  return (
    <>
      <p
        ref={textRef}
        id={id}
        className={`${className} clamp ${clamped ? "clamp--on" : ""}`.trim()}
        style={{ ["--clamp-lines" as string]: lines }}
      >
        {text}
      </p>

      {overflows && (
        <button
          type="button"
          className="clamp__toggle"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? "Read less" : "Read more"}
          <span className="visually-hidden"> about {label}</span>
        </button>
      )}
    </>
  );
}
